import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LuMinus, LuPlus } from "react-icons/lu";
import { inclGst } from "../../utils/priceText";

/**
 * Chawla Ispat design system ke chhote building blocks (canvas "Colour A").
 * Har storefront screen inhi se banta hai taaki button/chip/tag har jagah
 * ek jaise dikhein. Rang tailwind ke `ci-*` tokens se aate hain.
 */

type BtnVariant = "orange" | "navy" | "line" | "ghost";

const BTN_BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";

const BTN_VARIANT: Record<BtnVariant, string> = {
  orange: "border-ci-orange bg-ci-orange text-white hover:bg-ci-orange2 hover:border-ci-orange2",
  navy: "border-ci-navy bg-ci-navy text-white hover:bg-ci-navy2 hover:border-ci-navy2",
  line: "border-[#cfc9bf] bg-white text-ci-ink hover:border-ci-navy",
  ghost: "border-transparent bg-transparent text-ci-navy hover:bg-ci-bg",
};

export interface CiButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: "sm" | "md" | "lg";
}

export const CiButton: React.FC<CiButtonProps> = ({
  variant = "navy",
  size = "md",
  className = "",
  type = "button",
  children,
  ...rest
}) => {
  const sz =
    size === "sm"
      ? "h-[38px] px-4 text-[14px]"
      : size === "lg"
      ? "h-[52px] px-6 text-[16px]"
      : "h-[46px] px-5 text-[15px]";
  return (
    <button
      type={type}
      className={`${BTN_BASE} ${BTN_VARIANT[variant]} ${sz} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

export interface CiChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  dashed?: boolean;
}

const chipClass = (active: boolean, dashed: boolean, className: string) =>
  `inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[14px] font-medium transition ${
    active
      ? "border-ci-navy bg-ci-navy text-white"
      : "border-ci-line bg-white text-ci-ink hover:border-ci-navy"
  } ${dashed ? "border-dashed" : ""} ${className}`;

export const CiChip: React.FC<CiChipProps> = ({
  active = false,
  dashed = false,
  className = "",
  type = "button",
  children,
  ...rest
}) => (
  <button type={type} className={chipClass(active, dashed, className)} {...rest}>
    {children}
  </button>
);

/**
 * Wahi chip, par asli <a href>. Navigation wale chips isi se banao — crawler
 * onClick={() => navigate(...)} ko follow nahi karta, yaani un pages tak koi
 * link hi nahi pahunchta.
 */
export const CiChipLink: React.FC<
  {
    to: string;
    state?: any;
    active?: boolean;
    dashed?: boolean;
    className?: string;
    children?: React.ReactNode;
  } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">
> = ({ to, state, active = false, dashed = false, className = "", children, ...rest }) => (
  <Link to={to} state={state} className={chipClass(active, dashed, className)} {...rest}>
    {children}
  </Link>
);

export type TagTone = "ok" | "warn" | "navy" | "muted";

const TAG_TONE: Record<TagTone, string> = {
  ok: "bg-ci-okbg text-ci-ok",
  warn: "bg-ci-warnbg text-ci-warn",
  navy: "bg-[#e8edf4] text-ci-navy",
  muted: "bg-ci-bg text-ci-muted",
};

export const CiTag: React.FC<{
  tone?: TagTone;
  className?: string;
  children?: React.ReactNode;
}> = ({ tone = "navy", className = "", children }) => (
  <span
    className={`inline-flex h-[22px] items-center whitespace-nowrap rounded px-2 text-[12px] font-semibold ${TAG_TONE[tone]} ${className}`}
  >
    {children}
  </span>
);

/**
 * Rate line — owner ka faisla (14-Sep-2026): website par har rate GST ke SAATH.
 * `rate` prop ab bhi ex-GST (taxable) aata hai (API/quote wahi bhejte hain);
 * yahin gross karke bada number dikhate hain, neeche chhota "incl. 18% GST".
 * Ex-GST kahin nahi dikhta.
 */
export const RateLine: React.FC<{
  rate: number | null | undefined;
  unit?: string | null;
  gstPercent?: number | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}> = ({ rate, unit, gstPercent, size = "md", className = "" }) => {
  const r = Number(rate);
  if (!Number.isFinite(r) || r <= 0) {
    return (
      <span className={`text-[13px] text-ci-muted ${className}`}>
        Rate available nahi
      </span>
    );
  }
  const g = Number.isFinite(Number(gstPercent)) ? Number(gstPercent) : null;
  const big =
    size === "lg" ? "text-[40px]" : size === "sm" ? "text-[20px]" : "text-[26px]";
  const allIn = inclGst(r, g);
  return (
    <span className={`flex flex-col leading-none ${className}`}>
      <span className={`font-display font-bold text-ci-ink ci-num ${big}`}>
        ₹{allIn.toFixed(2)}
        <span className="ml-0.5 font-body text-[14px] font-medium text-ci-muted">
          /{unit || "unit"}
        </span>
      </span>
      <span className="mt-1 text-[12px] font-medium text-ci-muted">
        {g !== null ? `incl. ${g}% GST` : "incl. GST"}
      </span>
    </span>
  );
};

export interface QtyStepperProps {
  value: number;
  unit?: string | null;
  step?: number;
  min?: number;
  onChange: (next: number) => void;
  className?: string;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

/** − [ 2,400 kg ] + — design ka quantity stepper */
export const QtyStepper: React.FC<QtyStepperProps> = ({
  value,
  unit,
  step = 1,
  min = 0,
  onChange,
  className = "",
  size = "md",
  disabled = false,
}) => {
  const h = size === "lg" ? "h-[50px]" : size === "sm" ? "h-[38px]" : "h-[44px]";
  const w = size === "sm" ? "w-[36px]" : "w-[44px]";
  const round = (n: number) => Math.round(n * 1000) / 1000;
  const dec = () => onChange(Math.max(min, round(value - step)));
  const inc = () => onChange(round(value + step));

  // Typing ke beech ka adhoora text sirf yahin rehta hai. Pehle box khaali
  // karte hi Number("") === 0 upar chala jaata tha aur cart reducer us line ko
  // hata deta tha — user naya number type hi nahi kar paata tha.
  const [draft, setDraft] = useState<string | null>(null);
  const commit = (raw: string) => {
    setDraft(raw);
    if (raw.trim() === "") return;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) onChange(n);
  };
  return (
    <div
      className={`flex ${h} items-center overflow-hidden rounded-md border border-[#cfc9bf] bg-white ${className}`}
    >
      <button
        type="button"
        aria-label="Kam karein"
        disabled={disabled}
        onClick={dec}
        className={`flex ${w} h-full items-center justify-center border-r border-ci-line text-ci-navy hover:bg-ci-bg disabled:opacity-40`}
      >
        <LuMinus size={16} />
      </button>
      <label className="flex h-full flex-1 items-center justify-center gap-1 px-1">
        <input
          type="number"
          inputMode="decimal"
          value={draft !== null ? draft : Number.isFinite(value) ? value : ""}
          min={min}
          step={step}
          disabled={disabled}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setDraft(null)}
          className="ci-num w-[64px] bg-transparent text-center text-[15px] font-semibold text-ci-ink focus:outline-none"
        />
        {unit ? (
          <span className="text-[13px] font-medium text-ci-muted">{unit}</span>
        ) : null}
      </label>
      <button
        type="button"
        aria-label="Zyada karein"
        disabled={disabled}
        onClick={inc}
        className={`flex ${w} h-full items-center justify-center border-l border-ci-line text-ci-navy hover:bg-ci-bg disabled:opacity-40`}
      >
        <LuPlus size={16} />
      </button>
    </div>
  );
};

export const SectionTitle: React.FC<{
  children?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
}> = ({ children, right, className = "" }) => (
  <div className={`mb-4 flex items-baseline justify-between gap-4 ${className}`}>
    <h2 className="font-display text-[26px] font-bold uppercase leading-none text-ci-navy sm:text-[30px]">
      {children}
    </h2>
    {right ? <div className="text-[14px] font-medium text-ci-orange2">{right}</div> : null}
  </div>
);

/** Rupee format — ₹1,25,760.00 */
export const inrText = (n: number | string | null | undefined): string => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
